import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LeaderboardPlayAgain } from '../../components/LeaderboardPlayAgain.tsx';
import { gameService } from '../../services/gameService.ts';
import { PortfolioGameResult } from '../../types.ts';
import { FIRESWEEPER_SURVIVOR } from '../../constants/gameModes.ts';
import { useUtcCountdown } from '../../hooks/useUtcCountdown.ts';
import { SurvivorLeaderboardLocationState } from '../../contexts/PortfolioContext.tsx';
import { sortSurvivorLeaderboard } from '../../utils/survivorLeaderboard.ts';
import { buildSurvivorLeaderboardRows } from '../../utils/leaderboardDisplay.ts';

function SurvivorLeaderboardRow({
  rank,
  result,
  highlight,
}: {
  rank: number | string;
  result: PortfolioGameResult;
  highlight?: boolean;
}) {
  return (
    <tr className={highlight ? 'leaderboard-row-player' : undefined}>
      <td>{rank}</td>
      <td>
        <span className={`badge ${highlight ? 'badge-player' : 'badge-win'}`}>
          {result.selectedTileCount}
        </span>
      </td>
      <td>{result.duration}</td>
    </tr>
  );
}

export default function PortfolioLeaderboard() {
  const location = useLocation();
  const playerResult = (location.state as SurvivorLeaderboardLocationState | null)?.playerResult;
  const [leaderboard, setLeaderboard] = useState<PortfolioGameResult[]>([]);
  const [gamesPlayed, setGamesPlayed] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { dayKey, countdown } = useUtcCountdown();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [results, count] = await Promise.all([
          gameService.getPortfolioLeaderboard(500, dayKey),
          gameService.getPortfolioGamesCount(dayKey),
        ]);
        setLeaderboard(results);
        setGamesPlayed(count);
      } catch (error) {
        console.error('Error fetching portfolio leaderboard:', error);
        setGamesPlayed(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dayKey]);

  const sorted = useMemo(() => sortSurvivorLeaderboard(leaderboard), [leaderboard]);
  const displayRows = useMemo(
    () => buildSurvivorLeaderboardRows(sorted, playerResult),
    [sorted, playerResult]
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">Today&apos;s {FIRESWEEPER_SURVIVOR.subtitle} Board</h1>
        <p className="leaderboard-subtitle">
          {dayKey} UTC · Resets in {countdown}
        </p>
        <p className="leaderboard-plays">
          {gamesPlayed !== null ? (
            `${gamesPlayed.toLocaleString()} plays today`
          ) : (
            <span className="stat-shimmer" aria-label="Loading plays today" />
          )}
        </p>
        <p className="leaderboard-hint">{FIRESWEEPER_SURVIVOR.leaderboardHint}</p>
      </div>

      <LeaderboardPlayAgain to={FIRESWEEPER_SURVIVOR.path} />

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Tiles</th>
              <th>Duration(s)</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan={3} className="table-empty">
                  No wins yet today. Be the first to survive!
                </td>
              </tr>
            ) : (
              displayRows.map((row) => (
                <SurvivorLeaderboardRow
                  key={row.key}
                  rank={row.rank}
                  result={row.result}
                  highlight={row.highlight}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
